import React from 'react';
import classNames from 'classnames';
import Button from "./Button.js";
import Toggle from "./Toggle.js";
import NumericInput from "./NumericInput.js";
import TextInput from "./TextInput.js";
import MultiSelector from "./MultiSelector.js";
import DateTimePicker from "./DateTimePicker.js";
import DateTimeRangePicker from "./DateTimeRangePicker.js";
import Radio from "./RadioButton.js";
import TextArea from "./TextArea.js";
import Checkbox from "./Checkbox.js";
import Text from "./Text.js";
import FormValidator from "./FormValidator.js";
import Selector from "./Selector.js";
import FileInput from "./FileInput.js";
import Theme from "./Theme.js";
import { jsx as _jsx } from "react/jsx-runtime";
const Context = /*#__PURE__*/React.createContext(null);

/**
 * @typedef {Validator}
 * @property {object} fields an object containing the names of the fields and their restrictions
 * @property {function} fields[name].validator a function( value, name, formValues ) that returns 
 * true if the value is valid. If it is invalid, the function can return either false or a string
 * indicating why there is an error
 * @property {boolean} fields[name].active if false, the validator will be ignored
 * @property {function} fields[name].allow a function( value, name, formValues ) that returns 
 * true if the value should even be allowed. 
 * @property {function} [onValueChange] this is invoked as the user modifies values. You can
 * change values in the data object to allow for conditional changes based on other values.
 * @property {function} [validator] if supplied, this will be called if all the
 * field validators passed. It will be given the current data values object and can return
 * either true if there is no error, a string describing the overally error if there is one,
 * or an object containing the errors:
 * { 
 *   msg : "optional overall error message", 
 *   errors : [ 
 *     'fieldName', 
 *     { name : 'fieldName', msg : 'field error message' }
 *   ]
 * } 
 **/

/**
 * The form will manage a data object's draft state with
 * validating inputs. 
 * 
 * Props
 * @property {object} [data] the default form values/revert to state
 * @property {Validator} validator the validator options
 * @property {boolean} [autoUpdate] if true (default) the form will update
 * if there are no current changes and the data object is different
 * @property {function} render this function ( validator, loading ) is called with a validator and should
 * create the components that will be rendered in the form. Use validator.value( 'name' )
 * to conditionally hide and remove fields based on selections in the form. The
 * Form.Error component can be used to render field levels (if name prop is given) or
 * the overall error message (if name is not given). Instead of directly using
 * TextInput, Checkbox, etc, use Form.TextInput, Form.Checkbox, etc. Use Form.Submit
 * and Form.Revert to include submit and revert buttons.
 * @property {function} onSubmit called when a valid form is submitted
 * @property {boolean} scrollToError if true, the view will scroll to an input field in error
 * after a submit.
 */
export default class Form extends React.Component {
  constructor(props) {
    super(props);
    this.ref = /*#__PURE__*/React.createRef();
    this.state = {
      // eslint-disable-next-line react/no-unused-state
      form: {
        values: props.data
      }
    };
  }
  getContext(validator, loading) {
    if (this.cxt == null || this.cxt.validator !== validator || this.cxt.loading !== loading || this.cxt.state !== validator.getData()) {
      this.cxt = {
        validator,
        loading,
        state: validator.getData()
      };
    }
    return this.cxt;
  }
  getValidator() {
    const opts = this.props.validator;
    if (this.validator == null || this.validator.options !== opts) {
      this.validator = FormValidator.create(this, opts, {
        onRevert: this.onRevert
      });
    }
    return this.validator;
  }
  onRevert = evt => {
    const {
      onRevert,
      eventData
    } = this.props;
    if (typeof onRevert === 'function') {
      onRevert({
        data: eventData,
        value: evt
      });
    }
  };
  onSubmit = evt => {
    evt.preventDefault();
    evt.stopPropagation();
    const {
      onSubmit,
      onSubmitError,
      eventData,
      scrollToError = true
    } = this.props;
    const validator = this.getValidator();
    if (validator.isValid()) {
      if (validator.isSubmitValid()) {
        onSubmit({
          value: validator.getData().values,
          data: eventData
        });
        return;
      }
    }

    // The state is being set. We must let the setState() occur before we

    if (onSubmitError) {
      if (onSubmitError({
        value: validator.getData().values,
        data: eventData
      }) === false) {
        return;
      }
    }
    // can evaluate errors to perform a scroll to.
    if (scrollToError) {
      setTimeout(() => {
        const data = validator.getData();
        const {
          submitErrors
        } = data;
        let st = null;
        let y = 10000000;
        Object.entries(submitErrors).forEach(([k, v]) => {
          if (v === true) {
            const elem = this.ref.current.querySelector(`*[name="${k}"]`);
            if (elem.scrollIntoView && elem.getBoundingClientRect().top < y) {
              y = elem.getBoundingClientRect().top;
              st = elem;
            }
          }
        });
        if (st) {
          st.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 10);
    }
  };
  render() {
    // eslint-disable-next-line
    const {
      className,
      render,
      validator,
      onSubmit,
      autoUpdate,
      data,
      eventData,
      onRevert,
      loading,
      layoutOptions,
      ...rest
    } = this.props;
    const cn = classNames('Form', 'form', className);
    const v = this.getValidator();
    const cxt = this.getContext(v, loading);
    // If autoUpdate is false, only update the first time
    if (autoUpdate !== false || v.getDefaultData() == null) {
      v.setDefaultData(data);
    }
    return /*#__PURE__*/_jsx("form", {
      ref: this.ref,
      className: cn,
      ...rest,
      onSubmit: this.onSubmit,
      children: /*#__PURE__*/_jsx(Context.Provider, {
        value: cxt,
        children: render(v, loading)
      })
    });
  }
}
Form.Context = Context;
class Submit extends React.Component {
  static contextType = Context;
  render() {
    const {
      loading,
      validator
    } = this.context;
    const {
      disabled,
      disableOnNoChanges = true
    } = this.props;
    let nDisabled;
    if (disabled != null) {
      nDisabled = disabled;
    } else if (disableOnNoChanges) {
      nDisabled = loading || !validator.isValid() || !validator.hasChanges();
    } else {
      nDisabled = loading || !validator.isValid();
    }
    return /*#__PURE__*/_jsx(Button, {
      type: "submit",
      ...this.props,
      disabled: nDisabled
    });
  }
}
class Revert extends React.Component {
  static contextType = Context;
  render() {
    const {
      loading,
      validator
    } = this.context;
    const disabled = loading || validator.hasChanges() !== true;
    return /*#__PURE__*/_jsx(Button, {
      type: "button",
      ...this.props,
      disabled: disabled,
      onClick: validator.revert
    });
  }
}
class FormElement extends React.Component {
  static contextType = Context;
  constructor(props) {
    super(props);
    this.state = {
      initialValue: null,
      value: null
    };
  }
  static getDerivedStateFromProps(props, state) {
    if (state.initialValue != props.value) {
      return {
        value: props.value,
        initialValue: props.value
      };
    }
    return null;
  }
  render() {
    const {
      loading,
      validator
    } = this.context;
    const {
      disabled,
      name,
      eventData,
      controlled = false,
      element: Component,
      ...rest
    } = this.props;
    const fname = name || eventData;
    let onChange = validator.onInputChange;
    let {
      value
    } = this.state;
    if (controlled) {
      onChange = e => {
        const r = validator.onInputChange(e);
        this.setState({
          value: e.value
        });
        return r;
      };
    } else {
      value = validator.value(fname);
    }
    return /*#__PURE__*/_jsx(Component, {
      ...rest,
      ...validator.dataProps(fname),
      name: fname,
      eventData: fname,
      value: value,
      disabled: loading === true || disabled === true,
      onChange: onChange
    });
  }
}
class FormTextInput extends React.Component {
  static contextType = Context;
  render() {
    const {
      loading,
      validator
    } = this.context;
    const {
      disabled
    } = this.props;
    const name = this.props.name || this.props.eventData;
    return /*#__PURE__*/_jsx(TextInput, {
      ...this.props,
      ...validator.dataProps(name),
      name: name,
      eventData: name,
      value: validator.value(name),
      changeOnReturn: false,
      disabled: loading === true || disabled === true,
      onChange: validator.onInputChange
    });
  }
}
class FormNumInput extends React.Component {
  static contextType = Context;
  render() {
    const {
      loading,
      validator
    } = this.context;
    const {
      disabled,
      name,
      eventData,
      changeOnReturn,
      changeOnBlur,
      float,
      ...rest
    } = this.props;
    const fname = name || eventData;
    // for floats, we need to force changeOnReturn to be true so the '.' works
    // Actually, ints having issues too. just change on return and blue
    return /*#__PURE__*/_jsx(NumericInput, {
      ...rest,
      ...validator.dataProps(name),
      name: fname,
      eventData: name,
      float: float,
      value: validator.value(name),
      changeOnReturn: float === true ? true : changeOnReturn,
      changeOnBlur: float === true ? true : changeOnReturn,
      disabled: loading === true || disabled === true,
      onChange: validator.onInputChange
    });
  }
}
class FormTextArea extends React.Component {
  static contextType = Context;
  render() {
    const {
      loading,
      validator
    } = this.context;
    const {
      disabled
    } = this.props;
    const name = this.props.name || this.props.eventData;
    return /*#__PURE__*/_jsx(TextArea, {
      ...this.props,
      ...validator.dataProps(name),
      name: name,
      eventData: name,
      value: validator.value(name),
      disabled: loading === true || disabled === true,
      draggable: false,
      onChange: validator.onInputChange
    });
  }
}
class FormDateTimePicker extends React.Component {
  static contextType = Context;
  render() {
    const {
      loading,
      validator
    } = this.context;
    const {
      disabled
    } = this.props;
    const name = this.props.name || this.props.eventData;
    return /*#__PURE__*/_jsx(DateTimePicker, {
      ...this.props,
      ...validator.dataProps(name),
      name: name,
      eventData: name,
      value: validator.value(name),
      disabled: loading === true || disabled === true,
      onChange: validator.onInputChange
    });
  }
}
class FormDateTimeRangePicker extends React.Component {
  static contextType = Context;
  render() {
    const {
      loading,
      validator
    } = this.context;
    const {
      disabled
    } = this.props;
    const name = this.props.name || this.props.eventData;
    return /*#__PURE__*/_jsx(DateTimeRangePicker, {
      ...this.props,
      ...validator.dataProps(name),
      name: name,
      eventData: name,
      value: validator.value(name),
      disabled: loading === true || disabled === true,
      onChange: validator.onInputChange
    });
  }
}
class FormToggle extends React.Component {
  static contextType = Context;
  render() {
    const {
      loading,
      validator
    } = this.context;
    const {
      disabled
    } = this.props;
    const name = this.props.name || this.props.eventData;
    return /*#__PURE__*/_jsx(Toggle, {
      ...this.props,
      name: name,
      eventData: name,
      value: validator.value(name) || false,
      disabled: loading === true || disabled === true,
      onChange: validator.onInputChange
    });
  }
}
class FormSelector extends React.Component {
  static contextType = Context;
  render() {
    const {
      loading,
      validator
    } = this.context;
    const {
      disabled
    } = this.props;
    const name = this.props.name || this.props.eventData;
    return /*#__PURE__*/_jsx(Selector, {
      ...this.props,
      ...validator.dataProps(name),
      name: name,
      eventData: name,
      value: validator.value(name),
      disabled: loading === true || disabled === true,
      onChange: validator.onInputChange
    });
  }
}
class FormRadio extends React.Component {
  static contextType = Context;
  render() {
    const {
      loading,
      validator
    } = this.context;
    const {
      disabled
    } = this.props;
    const name = this.props.name || this.props.eventData;
    return /*#__PURE__*/_jsx(Radio, {
      ...this.props,
      name: name,
      eventData: name,
      value: validator.value(name) || false,
      disabled: loading === true || disabled === true,
      onChange: validator.onInputChange
    });
  }
}
class FormCheckbox extends React.Component {
  static contextType = Context;
  render() {
    const {
      loading,
      validator
    } = this.context;
    const {
      disabled
    } = this.props;
    const name = this.props.name || this.props.eventData;
    return /*#__PURE__*/_jsx(Checkbox, {
      ...this.props,
      name: name,
      eventData: name,
      value: validator.value(name) || false,
      disabled: loading === true || disabled === true,
      onChange: validator.onInputChange
    });
  }
}
class FormMultiSelector extends React.Component {
  static contextType = Context;
  render() {
    const {
      loading,
      validator
    } = this.context;
    const {
      disabled,
      name: pName,
      eventData,
      allowCreate = true
    } = this.props;
    const name = pName || eventData;
    return /*#__PURE__*/_jsx(MultiSelector, {
      ...this.props,
      ...validator.dataProps(name),
      allowCreate: allowCreate,
      name: name,
      eventData: name,
      value: validator.value(name),
      disabled: loading === true || disabled === true,
      onChange: validator.onInputChange
    });
  }
}
class FormFileInput extends React.Component {
  static contextType = Context;
  render() {
    const {
      loading,
      validator
    } = this.context;
    const {
      t: dud,
      disabled,
      eventData,
      ...otherProps
    } = this.props;
    let {
      t,
      name
    } = this.props;
    if (!t) {
      t = Theme.I18N.ns('Form').t;
    }
    name = name || eventData;
    const value = validator.value(name);
    return /*#__PURE__*/_jsx(FileInput, {
      ...otherProps,
      name: name,
      eventData: name,
      value: validator.value(name),
      disabled: loading === true || disabled === true,
      onChange: validator.onInputChange,
      children: t('fileInputText', `${value?.length || 0} Files Selected`, {
        count: value?.length || 0,
        name: value && value[0]?.name
      })
    });
  }
}
class FormText extends React.Component {
  static contextType = Context;
  render() {
    const {
      validator
    } = this.context;
    const name = this.props.name || this.props.eventData;
    return /*#__PURE__*/_jsx(Text, {
      ...this.props,
      name: name,
      children: validator.value(name)
    });
  }
}
class FormError extends React.Component {
  static contextType = Context;
  render() {
    const {
      loading,
      validator
    } = this.context;
    const {
      variant = 'formError',
      alwaysTakeSpace = true
    } = this.props;
    const name = this.props.name || this.props.eventData;
    let err = loading ? null : validator.message(name);
    if (!alwaysTakeSpace && err == null) {
      return null;
    }
    const style = {};
    if (err == null || err.trim().length === 0) {
      err = '-';
      style.visibility = 'hidden';
    }
    return /*#__PURE__*/_jsx(Text, {
      ...this.props,
      variant: variant,
      name: name,
      style: style,
      children: err
    });
  }
}
Form.Submit = Submit;
Form.Revert = Revert;
Form.TextInput = FormTextInput;
Form.NumericInput = FormNumInput;
Form.DateTimePicker = FormDateTimePicker;
Form.DateTimeRangePicker = FormDateTimeRangePicker;
Form.Toggle = FormToggle;
Form.MultiSelector = FormMultiSelector;
Form.Radio = FormRadio;
Form.Checkbox = FormCheckbox;
Form.TextArea = FormTextArea;
Form.Text = FormText;
Form.Error = FormError;
Form.Element = FormElement;
Form.Selector = FormSelector;
Form.FileInput = FormFileInput;